import { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { UserGear, UserPlus, PencilSimple, Trash, FloppyDisk, X, ShieldCheck, User } from '@phosphor-icons/react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { useAuth } from '@/context/AuthContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

import { ModulePicker, MODULES, DEFAULT_MODULES } from './users/ModulePicker';

const UsersAdmin = () => {
  const { user: currentUser } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [addOpen, setAddOpen] = useState(false);
  const [editTarget, setEditTarget] = useState(null);
  const [form, setForm] = useState({ email: '', password: '', role: 'user', permissions: [...DEFAULT_MODULES] });

  const fetchUsers = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/users`);
      setUsers(data);
    } catch {
      toast.error('Failed to load users');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchUsers(); }, [fetchUsers]);

  const openAdd = () => { setForm({ email: '', password: '', role: 'user', permissions: [...DEFAULT_MODULES] }); setAddOpen(true); };
  const openEdit = (u) => { setEditTarget(u); setForm({ email: u.email, password: '', role: u.role, permissions: [...(u.permissions || [])] }); };
  const samePerms = (a, b) => [...a].sort().join() === [...b].sort().join();

  const handleAdd = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/users`, form);
      toast.success(`User ${form.email} created`);
      setAddOpen(false);
      fetchUsers();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create user');
    }
  };

  const handleEdit = async (e) => {
    e.preventDefault();
    const payload = {};
    if (form.email && form.email !== editTarget.email) payload.email = form.email;
    if (form.password) payload.password = form.password;
    if (form.role && form.role !== editTarget.role) payload.role = form.role;
    if (form.role === 'user' && !samePerms(form.permissions, editTarget.permissions || [])) payload.permissions = form.permissions;
    if (Object.keys(payload).length === 0) { toast.info('Nothing changed'); return; }
    try {
      await axios.patch(`${API}/users/${editTarget.id}`, payload);
      toast.success(`User ${editTarget.email} updated`);
      setEditTarget(null);
      fetchUsers();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update user');
    }
  };

  const handleDelete = async (u) => {
    if (!window.confirm(`Delete user ${u.email}? This cannot be undone.`)) return;
    try {
      await axios.delete(`${API}/users/${u.id}`);
      toast.success('User deleted');
      fetchUsers();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete user');
    }
  };

  return (
    <div data-testid="users-page">
      <div className="mb-8 flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
        <div>
          <h1 className="text-4xl sm:text-5xl font-black tracking-tighter text-white mb-2" style={{fontFamily: 'Cabinet Grotesk, sans-serif'}}>
            Users
          </h1>
          <p className="text-base font-medium text-white">Manage who can access this CRM. Owners get full access; Users can only enter candidate data.</p>
        </div>
        <Button
          data-testid="add-user-button"
          onClick={openAdd}
          className="bg-[#A3E635] text-[#0A0A0A] border-2 border-[#0A0A0A] rounded-md font-bold px-4 py-2 shadow-[2px_2px_0px_0px_rgba(10,10,10,1)] hover:-translate-y-0.5 hover:shadow-[4px_4px_0px_0px_rgba(10,10,10,1)] transition-all"
        >
          <UserPlus size={20} weight="bold" className="mr-2" />
          Add User
        </Button>
      </div>

      {loading ? (
        <div className="text-white font-bold">Loading…</div>
      ) : (
        <div className="bg-[#0F3622] border-2 border-[#A3E635] rounded-md shadow-[4px_4px_0px_0px_rgba(163,230,53,0.3)] overflow-x-auto">
          <table className="w-full tracker-table" data-testid="users-table">
            <thead>
              <tr>
                <th>Email</th>
                <th>Role</th>
                <th>Tabs Allowed</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {users.length === 0 ? (
                <tr><td colSpan="4" className="text-center py-6 text-white">No users yet.</td></tr>
              ) : (
                users.map((u) => {
                  const isSelf = currentUser?.id === u.id;
                  const isOwner = u.role === 'owner';
                  return (
                    <tr key={u.id} data-testid={`user-row-${u.id}`}>
                      <td className="font-bold">
                        {u.email}
                        {isSelf && <span className="ml-2 text-[10px] font-bold px-1.5 py-0.5 bg-[#FDE047] text-black rounded">YOU</span>}
                      </td>
                      <td>
                        <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-md text-xs font-bold border-2 border-black ${isOwner ? 'bg-[#FDE047] text-black' : 'bg-[#93C5FD] text-black'}`}>
                          {isOwner ? <ShieldCheck size={14} weight="fill" /> : <User size={14} weight="fill" />}
                          {u.role.toUpperCase()}
                        </span>
                      </td>
                      <td>
                        {isOwner ? (
                          <span className="text-xs font-bold text-[#A3E635]">Full (owner)</span>
                        ) : (
                          <div className="flex flex-wrap gap-1" data-testid={`modules-${u.id}`}>
                            {(u.permissions || []).length === 0 && <span className="text-[10px] font-bold text-[#ccc]">Dashboard only</span>}
                            {MODULES.filter((m) => (u.permissions || []).includes(m.key)).map((m) => (
                              <span key={m.key} data-testid={`module-chip-${u.id}-${m.key}`} className="px-1.5 py-0.5 rounded border border-black text-[10px] font-black text-black" style={{ backgroundColor: m.color }}>{m.label}</span>
                            ))}
                          </div>
                        )}
                      </td>
                      <td>
                        <div className="flex gap-1.5">
                          <button
                            data-testid={`edit-user-${u.id}`}
                            onClick={() => openEdit(u)}
                            title="Edit user (email / password / role)"
                            className="p-2 bg-[#93C5FD] border-2 border-black rounded hover:-translate-y-0.5 hover:shadow-[2px_2px_0px_0px_rgba(10,10,10,1)] transition-all"
                          >
                            <PencilSimple size={14} weight="bold" />
                          </button>
                          {!isSelf && (
                            <button
                              data-testid={`delete-user-${u.id}`}
                              onClick={() => handleDelete(u)}
                              title="Delete user"
                              className="p-2 bg-[#F87171] text-white border-2 border-black rounded hover:-translate-y-0.5 hover:shadow-[2px_2px_0px_0px_rgba(10,10,10,1)] transition-all"
                            >
                              <Trash size={14} weight="bold" />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* Add Dialog */}
      <Dialog open={addOpen} onOpenChange={setAddOpen}>
        <DialogContent className="bg-white border-2 border-[#0A0A0A] shadow-[6px_6px_0px_0px_rgba(10,10,10,1)] sm:max-w-[460px]">
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold text-[#0A0A0A] flex items-center gap-2" style={{fontFamily: 'Cabinet Grotesk, sans-serif'}}>
              <UserGear size={26} weight="duotone" />
              Add New User
            </DialogTitle>
            <DialogDescription className="sr-only">Dialog content</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleAdd} className="space-y-4">
            <div>
              <Label className="text-xs font-bold uppercase tracking-wider text-[#0A0A0A]">Email</Label>
              <Input
                data-testid="add-user-email"
                type="email" required value={form.email}
                onChange={(e) => setForm({...form, email: e.target.value})}
                className="bg-white text-[#0A0A0A] border-2 border-[#0A0A0A] rounded-md px-3 py-2"
              />
            </div>
            <div>
              <Label className="text-xs font-bold uppercase tracking-wider text-[#0A0A0A]">Password (min 6 chars)</Label>
              <Input
                data-testid="add-user-password"
                type="password" required minLength={6} value={form.password}
                onChange={(e) => setForm({...form, password: e.target.value})}
                className="bg-white text-[#0A0A0A] border-2 border-[#0A0A0A] rounded-md px-3 py-2"
              />
            </div>
            <div>
              <Label className="text-xs font-bold uppercase tracking-wider text-[#0A0A0A]">Role</Label>
              <select
                data-testid="add-user-role"
                value={form.role}
                onChange={(e) => setForm({...form, role: e.target.value})}
                className="w-full bg-white text-[#0A0A0A] border-2 border-[#0A0A0A] rounded-md px-3 py-2 font-bold cursor-pointer [&>option]:bg-white [&>option]:text-[#0A0A0A]"
              >
                <option value="user">User — access only to the tabs you tick below</option>
                <option value="owner">Owner — full access (finance, billing, users)</option>
              </select>
            </div>
            {form.role === 'user' && (
              <ModulePicker value={form.permissions} onChange={(p) => setForm({...form, permissions: p})} testIdPrefix="add-user" />
            )}
            <div className="flex gap-2 pt-2">
              <Button
                data-testid="add-user-submit"
                type="submit"
                className="flex-1 bg-[#A3E635] text-black border-2 border-black rounded-md font-bold px-4 py-2 shadow-[2px_2px_0px_0px_rgba(10,10,10,1)] hover:-translate-y-0.5 hover:shadow-[4px_4px_0px_0px_rgba(10,10,10,1)] transition-all"
              >
                <FloppyDisk size={18} weight="bold" className="mr-2" />
                Create User
              </Button>
              <Button
                type="button" onClick={() => setAddOpen(false)}
                className="bg-[#404040] text-white border-2 border-black rounded-md font-bold px-4 py-2"
              >
                <X size={18} weight="bold" />
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog open={!!editTarget} onOpenChange={(v) => !v && setEditTarget(null)}>
        <DialogContent className="bg-white border-2 border-[#0A0A0A] shadow-[6px_6px_0px_0px_rgba(10,10,10,1)] sm:max-w-[460px]">
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold text-[#0A0A0A] flex items-center gap-2" style={{fontFamily: 'Cabinet Grotesk, sans-serif'}}>
              <PencilSimple size={26} weight="duotone" />
              Edit User
            </DialogTitle>
            <DialogDescription className="sr-only">Dialog content</DialogDescription>
          </DialogHeader>
          {editTarget && (
            <form onSubmit={handleEdit} className="space-y-4">
              <div>
                <Label className="text-xs font-bold uppercase tracking-wider text-[#0A0A0A]">Email</Label>
                <Input
                  data-testid="edit-user-email"
                  type="email" required value={form.email}
                  onChange={(e) => setForm({...form, email: e.target.value})}
                  className="bg-white text-[#0A0A0A] border-2 border-[#0A0A0A] rounded-md px-3 py-2"
                />
              </div>
              <div>
                <Label className="text-xs font-bold uppercase tracking-wider text-[#0A0A0A]">
                  New Password <span className="text-[#666] font-medium">(leave blank to keep current)</span>
                </Label>
                <Input
                  data-testid="edit-user-password"
                  type="password" minLength={6} value={form.password} placeholder="••••••"
                  onChange={(e) => setForm({...form, password: e.target.value})}
                  className="bg-white text-[#0A0A0A] border-2 border-[#0A0A0A] rounded-md px-3 py-2"
                />
              </div>
              <div>
                <Label className="text-xs font-bold uppercase tracking-wider text-[#0A0A0A]">Role</Label>
                <select
                  data-testid="edit-user-role"
                  value={form.role}
                  onChange={(e) => setForm({...form, role: e.target.value})}
                  className="w-full bg-white text-[#0A0A0A] border-2 border-[#0A0A0A] rounded-md px-3 py-2 font-bold cursor-pointer [&>option]:bg-white [&>option]:text-[#0A0A0A]"
                >
                  <option value="user">User</option>
                  <option value="owner">Owner</option>
                </select>
              </div>
              {form.role === 'user' && (
                <ModulePicker value={form.permissions} onChange={(p) => setForm({...form, permissions: p})} testIdPrefix="edit-user" />
              )}
              <div className="flex gap-2 pt-2">
                <Button
                  data-testid="edit-user-submit"
                  type="submit"
                  className="flex-1 bg-[#A3E635] text-black border-2 border-black rounded-md font-bold px-4 py-2 shadow-[2px_2px_0px_0px_rgba(10,10,10,1)] hover:-translate-y-0.5 hover:shadow-[4px_4px_0px_0px_rgba(10,10,10,1)] transition-all"
                >
                  <FloppyDisk size={18} weight="bold" className="mr-2" />
                  Save Changes
                </Button>
                <Button
                  type="button" onClick={() => setEditTarget(null)}
                  className="bg-[#404040] text-white border-2 border-black rounded-md font-bold px-4 py-2"
                >
                  <X size={18} weight="bold" />
                </Button>
              </div>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default UsersAdmin;
